import {
  dinero,
  add,
  subtract,
  multiply,
  type Dinero,
  type DineroCurrency,
  toDecimal,
} from "dinero.js";
import * as Currencies from "@dinero.js/currencies";
import type { Currency } from "@dinero.js/currencies";

/**
 * Get Dinero currency definition safely
 */
export function getCurrency(code: string): DineroCurrency<number> {
  const currency = Currencies[code as keyof typeof Currencies];
  if (!currency) {
    throw new Error(`Unsupported currency: ${code}`);
  }
  return currency;
}

/**
 * Get subunit factor (e.g. SAR → 100)
 */
export function getSubUnit(code: string): number {
  const currency = getCurrency(code);
  return Math.pow(10, currency.exponent);
}

/**
 * Validate the number of decimal places in the amount
 */
function validateDecimalPlaces(amount: number, currency: string) {
  const exponent = getCurrency(currency).exponent;
  const decimals = amount.toString().split(".")[1]?.length ?? 0;

  if (decimals > exponent) {
    throw new Error(
      `Amount has too many decimal places. Maximum allowed is ${exponent}.`
    );
  }
}

/**
 * Convert major units (e.g. 5.25 SAR) → minor units (525)
 */
export function majorToMinor(amount: number, currency: string): number {
  validateDecimalPlaces(amount, currency);
  if (!Number.isFinite(amount)) {
    throw new Error("Amount must be a finite number");
  }

  const subUnit = getSubUnit(currency);
  return Math.round(amount * subUnit);
}

/**
 * Apply percentage to a minor-unit amount
 * ALWAYS truncates
 */
export function applyPercentage(
  baseMinor: number,
  percentage: number // e.g. 5 = 5%
): number {
  if (percentage <= 0) return 0;

  return Math.floor((baseMinor * percentage) / 100);
}

/**
 * Convert minor → major for display (rounded)
 * This function may increase value -> that's OK only for final totals.
 * for Order total, Payment total, etc.
 * ❌ DO NOT USE FOR CALCULATIONS
 */
export function minorToMajorForDisplayRounded({
  amountMinor,
  currency,
  decimals = 2,
}: {
  amountMinor: number;
  currency: string;
  decimals?: number;
}): string {
  const subUnit = getSubUnit(currency);
  return (amountMinor / subUnit).toFixed(decimals);
}

/**
 * Convert minor → major (TRUNCATED)
 * Use for discounts, fees, breakdowns
 * Never increases value, always truncates
 * ❌ DO NOT STORE RESULT IN DB
 */
export function minorToMajorForBreakdownTruncated({
  amountMinor,
  currency,
  decimals = 2,
}: {
  amountMinor: number;
  currency: string;
  decimals?: number;
}): number {
  const subUnit = getSubUnit(currency);
  const value = amountMinor / subUnit;
  const factor = Math.pow(10, decimals);
  const truncatedResult = Math.floor(value * factor) / factor;

  return Number(truncatedResult.toFixed(decimals));
}

// ------------------------------------------------------------
// !Helper functions using dinero.js
// ------------------------------------------------------------

/**
 * Create Dinero from major units entered by admin
 * STRICT: decimals must match currency exponent
 */
export function dineroFromMajor({
  amount,
  currencyCode,
}: {
  amount: number;
  currencyCode: string;
}): Dinero<number> {
  if (!Number.isFinite(amount)) {
    throw new Error("Amount must be finite");
  }

  const currency = getCurrency(currencyCode);
  const factor = Math.pow(10, currency.exponent);

  if (!Number.isInteger(amount * factor)) {
    throw new Error(`Too many decimals. Max is ${currency.exponent}`);
  }

  return dinero({
    amount: amount * factor,
    currency,
  });
}

/**
 * Create Dinero from minor units (integer stored in DB).
 */
export function dineroFromMinor({
  amountMinor,
  currencyCode,
}: {
  amountMinor: number;
  currencyCode: string;
}): Dinero<number> {
  if (!Number.isInteger(amountMinor)) {
    throw new Error("Minor amount must be an integer");
  }

  return dinero({
    amount: amountMinor,
    currency: getCurrency(currencyCode),
  });
}

/**
 * Apply flat amount to a base amount
 * If isDiscount is true, subtract the flat amount
 * If isDiscount is false, add the flat amount
 */
export function applyFlat({
  base,
  flat,
  isDiscount,
}: {
  base: Dinero<number>;
  flat: Dinero<number>;
  isDiscount: boolean;
}): Dinero<number> {
  return isDiscount ? subtract(base, flat) : add(base, flat);
}

/**
 * Apply percentage to a Dinero amount
 * ALWAYS truncates (never increases value)
 */
export function applyPercentageWithDinero({
  base,
  percentage,
}: {
  base: Dinero<number>;
  percentage: number; // e.g. 5 = 5%
}): Dinero<number> {
  if (percentage <= 0) {
    return multiply(base, 0);
  }

  return multiply(base, percentage / 100);
}

/**
 * Calculate per-item tax (truncated) and totals from minor units.
 * Tax is calculated per item, then multiplied by quantity.
 */
export function calculateItemTotalsWithTax({
  amountMinor,
  quantity,
  currencyCode,
  taxRate,
  isTaxable,
}: {
  amountMinor: number;
  quantity: number;
  currencyCode: string;
  taxRate: number;
  isTaxable: boolean;
}): {
  singleItemTotalMinor: number;
  singleItemTaxMinor: number;
  itemsSubTotalMinor: number;
  itemsTaxMinor: number;
} {
  if (!Number.isInteger(quantity) || quantity < 0) {
    throw new Error("Quantity must be a non-negative integer");
  }

  const singleItemTotal = dineroFromMinor({
    amountMinor,
    currencyCode,
  });

  let singleItemTax = dineroFromMinor({
    amountMinor: 0,
    currencyCode,
  });

  if (isTaxable) {
    singleItemTax = applyPercentageWithDinero({
      base: singleItemTotal,
      percentage: taxRate,
    });
  }

  const itemsSubTotal = multiply(singleItemTotal, { amount: quantity });
  const itemsTax = multiply(singleItemTax, { amount: quantity });

  return {
    singleItemTotalMinor: dineroToMinor(singleItemTotal),
    singleItemTaxMinor: dineroToMinor(singleItemTax),
    itemsSubTotalMinor: dineroToMinor(itemsSubTotal),
    itemsTaxMinor: dineroToMinor(itemsTax),
  };
}

/**
 * Sum an array of Dinero objects
 */
export function sumDinero(values: Dinero<number>[]): Dinero<number> {
  return values.reduce((acc, v) => add(acc, v));
}

/**
 * Convert a Dinero object to a minor-unit number
 */
export function dineroToMinor(value: Dinero<number>): number {
  return value.toJSON().amount;
}

/**
 * UI display ONLY
 * ❌ Do not use for calculations
 */
export function dineroToDisplayRounded(
  value: Dinero<number>,
  decimals = 2
): number {
  return Number(
    toDecimal(value, ({ value }) => {
      return Number(value).toFixed(decimals);
    })
  );
}

/**
 * Convert a Dinero object to a major-unit number (truncated)
 * Use for breakdowns, discounts, fees
 * ❌ DO NOT STORE RESULT IN DB
 */
export function dineroToDisplayTruncated(
  value: Dinero<number>,
  decimals = 2
): number {
  const factor = Math.pow(10, decimals);
  const major =
    value.toJSON().amount / Math.pow(10, value.toJSON().currency.exponent);
  return Math.floor(major * factor) / factor;
}
